// src/components/Header.tsx (FIXED - Updated to use wallet context properly)
import React, { useState, useEffect } from 'react';
import { Wallet, Loader2, Menu, X, Home, Rocket, Zap, Package, BarChart3, Smartphone } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useWallet } from '../contexts/WalletContext';

const Header: React.FC = () => {
  const { isConnected, publicKey, balance, connecting, connect, disconnect } = useWallet();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isInPhantomBrowser, setIsInPhantomBrowser] = useState(false);
  const location = useLocation();

  const navItems = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/create-token', label: 'Create Token', icon: Rocket },
    { path: '/tools', label: 'Tools', icon: Zap },
    { path: '/bundles', label: 'My Bundles', icon: Package },
    { path: '/dashboard', label: 'Dashboard', icon: BarChart3 },
  ];

  useEffect(() => {
    const mobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const inPhantom = !!(window as any).solana?.isPhantom;
    
    setIsMobile(mobile);
    setIsInPhantomBrowser(mobile && inPhantom);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const formatAddress = (address: string) => {
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
  };

  const formatBalance = (bal: number) => {
    return bal < 0.0001 ? '< 0.0001' : bal.toFixed(4);
  };

  // FIXED: Use the wallet context connect method properly
  const handleConnect = async () => {
    try {
      await connect(); // This will handle both mobile deeplink and desktop connection
    } catch (error) {
      console.error('Connection failed:', error);
      // The connect method already handles error display
    }
    setIsMobileMenuOpen(false);
  };

  const handleDisconnect = async () => {
    await disconnect();
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  const getConnectButtonText = () => {
    if (connecting) return 'Connecting...';
    if (isMobile && !isInPhantomBrowser) return 'Connect Phantom';
    return 'Connect Phantom';
  };

  return (
    <>
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-gray-900/80 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo Section */}
            <div className="flex items-center space-x-3 sm:space-x-4">
              <Link to="/" className="flex items-center space-x-3 sm:space-x-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center">
                  <img 
                    src="/lamp.png" 
                    alt="Aladdin Logo" 
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement!.innerHTML = `
                        <div class="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                          <span class="text-white text-2xl sm:text-3xl">🧞</span>
                        </div>
                      `;
                    }}
                  />
                </div>
                <span 
                  className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" 
                  style={{ fontFamily: 'Cinzel, serif' }}
                >
                  Aladdin
                </span>
              </Link>
            </div>

            {/* Desktop Navigation and Wallet Section */}
            <div className="hidden xl:flex items-center space-x-6">
              {/* Navigation Links */}
              <nav className="flex items-center space-x-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${
                        location.pathname === item.path
                          ? 'bg-purple-600/20 text-purple-400'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>

              {/* Wallet Connection */}
              {isConnected ? (
                <div className="flex items-center space-x-3">
                  <div className="text-right">
                    <div className="text-sm text-gray-400">Balance</div>
                    <div className="font-semibold">{formatBalance(balance)} SOL</div>
                  </div>
                  <button
                    onClick={disconnect}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center space-x-2"
                  >
                    <Wallet className="w-4 h-4" />
                    <span>{formatAddress(publicKey!)}</span>
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center space-x-2 disabled:opacity-50"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Connecting...</span>
                    </>
                  ) : (
                    <>
                      <Wallet className="w-4 h-4" />
                      <span>Connect Phantom</span>
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="xl:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg hover:bg-gray-800 transition-colors relative z-50"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" />
                ) : (
                  <Menu className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="xl:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className="xl:hidden fixed top-16 left-0 right-0 bg-gray-900 border-b border-gray-800 z-40 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="px-4 py-4">
              {/* Mobile indicator for deeplink */}
              {isMobile && !isInPhantomBrowser && !isConnected && (
                <div className="mb-4 p-3 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Smartphone className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-blue-400">
                      Tap "Connect Phantom" to connect via deeplink
                    </span>
                  </div>
                </div>
              )}

              {/* Mobile Navigation */}
              <nav className="space-y-2 mb-4">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={handleNavClick}
                      className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-all ${
                        location.pathname === item.path
                          ? 'bg-purple-600/20 text-purple-400'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </nav>

              {/* Divider */}
              <div className="border-t border-gray-800 my-4"></div>

              {/* Wallet Section */}
              {isConnected ? (
                <div className="space-y-4">
                  <div className="bg-gray-800/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-400">Wallet</span>
                      <span className="font-mono text-sm">{formatAddress(publicKey!)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">Balance</span>
                      <span className="font-semibold">{formatBalance(balance)} SOL</span>
                    </div>
                  </div>

                  <button
                    onClick={handleDisconnect}
                    className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center space-x-2"
                  >
                    <Wallet className="w-4 h-4" />
                    <span>Disconnect Wallet</span>
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="w-full px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {connecting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Connecting...</span>
                    </>
                  ) : (
                    <>
                      <Wallet className="w-4 h-4" />
                      <span>{getConnectButtonText()}</span>
                    </>
                  )}
                </button>
              )}

              {/* Mobile Quick Stats (when connected) */}
              {isConnected && (
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <div className="bg-gray-800/50 rounded-lg p-2 text-center">
                    <div className="text-xs text-gray-400">Network</div>
                    <div className="text-sm font-medium text-purple-400">
                      {process.env.REACT_APP_SOLANA_RPC_URL?.includes('devnet') ? 'Devnet' : 'Mainnet'}
                    </div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-2 text-center">
                    <div className="text-xs text-gray-400">Status</div>
                    <div className="text-sm font-medium text-green-400">Connected</div>
                  </div>
                </div>
              )}

              {/* In Phantom Browser Indicator */}
              {isInPhantomBrowser && (
                <div className="mt-4 text-xs text-center text-gray-500">
                  Running in Phantom Browser ✓
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Header;